import React, { useMemo, useRef } from 'react';
import { FormField, Select, Textarea, Checkbox, MultiSelect } from '@/shared/components/ui';
import { useContactsDropdown } from '@/modules/contacts/hooks/useContactsDropdown';
import { useChannelsDropdown } from '@/modules/channels/hooks/useChannelsDropdown';
import type { ProjectNotifyEvents } from '../../../types';

/** Eventos que disparam mensagem ao contato (mesma ordem no form e no config). */
export const NOTIFY_EVENT_ITEMS: {
  key: keyof ProjectNotifyEvents;
  label: string;
  description?: string;
}[] = [
  { key: 'milestoneCompleted', label: 'Marco concluído' },
  { key: 'phaseCompleted', label: 'Fase concluída' },
  { key: 'projectCompleted', label: 'Projeto concluído' },
  {
    key: 'taskCompleted',
    label: 'Cada tarefa concluída',
    description: '(pode gerar muitas mensagens)',
  },
];

/** Variáveis suportadas no template da mensagem. */
export const NOTIFY_TEMPLATE_VARS = [
  '{{contato}}',
  '{{projeto}}',
  '{{evento}}',
  '{{tarefa}}',
  '{{fase}}',
  '{{progresso}}',
];

export interface NotifyContactFieldsProps {
  contactIds: string[];
  onContactIdsChange: (ids: string[]) => void;
  channelId: string | undefined;
  onChannelIdChange: (id: string | undefined) => void;
  events: ProjectNotifyEvents;
  onToggleEvent: (key: keyof ProjectNotifyEvents) => void;
  messageTemplate: string;
  onMessageTemplateChange: (value: string) => void;
  /** Prefixo dos data-testid (default `project-notify`). */
  testIdPrefix?: string;
}

/**
 * Campos de "Mensagens ao contato" — contatos + canal + eventos + template.
 * Componente presentacional (state-agnostic) reutilizado pelo formulário de
 * criação (Task 12) e pela aba de configurações do workspace (Task 13).
 */
export const NotifyContactFields: React.FC<NotifyContactFieldsProps> = ({
  contactIds,
  onContactIdsChange,
  channelId,
  onChannelIdChange,
  events,
  onToggleEvent,
  messageTemplate,
  onMessageTemplateChange,
  testIdPrefix = 'project-notify',
}) => {
  const { data: contacts, isLoading: loadingContacts } = useContactsDropdown();
  const { data: channels, isLoading: loadingChannels } = useChannelsDropdown();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const contactOptions = useMemo(
    () => (contacts ?? []).map((c) => ({ value: c.id, label: c.name })),
    [contacts],
  );

  const channelOptions = useMemo(
    () => (channels ?? []).map((c) => ({ value: c.id, label: c.name })),
    [channels],
  );

  /** Insere a variável na posição do cursor (ou ao final). */
  const insertVariable = (variable: string) => {
    const el = textareaRef.current;
    if (!el) {
      onMessageTemplateChange(`${messageTemplate}${variable}`);
      return;
    }
    const start = el.selectionStart ?? messageTemplate.length;
    const end = el.selectionEnd ?? messageTemplate.length;
    const next = messageTemplate.slice(0, start) + variable + messageTemplate.slice(end);
    onMessageTemplateChange(next);
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + variable.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField label="Contatos">
          <div data-testid={`${testIdPrefix}-contacts`}>
            <MultiSelect
              options={contactOptions}
              selectedValues={contactIds}
              onChange={onContactIdsChange}
              loading={loadingContacts}
              placeholder="Selecione os contatos"
            />
          </div>
        </FormField>

        <Select
          label="Canal de envio"
          options={channelOptions}
          value={channelId ?? ''}
          onChange={(v) => onChannelIdChange(v || undefined)}
          placeholder={loadingChannels ? 'Carregando...' : 'Selecione o canal'}
          data-testid={`${testIdPrefix}-channel`}
        />
      </div>

      <FormField label="Enviar mensagem quando">
        <div className="space-y-2.5">
          {NOTIFY_EVENT_ITEMS.map((item) => (
            <Checkbox
              key={item.key}
              label={item.label}
              {...(item.description ? { description: item.description } : {})}
              checked={events[item.key]}
              onChange={() => onToggleEvent(item.key)}
              data-testid={`${testIdPrefix}-event-${item.key}`}
            />
          ))}
        </div>
      </FormField>

      <FormField
        label="Template da mensagem"
        description="Use as variáveis abaixo para personalizar. Clique para inserir."
      >
        <Textarea
          ref={textareaRef}
          value={messageTemplate}
          onChange={(e) => onMessageTemplateChange(e.target.value)}
          rows={4}
          data-testid={`${testIdPrefix}-template`}
        />
        <div className="flex flex-wrap gap-1.5 mt-2">
          {NOTIFY_TEMPLATE_VARS.map((variable) => (
            <button
              key={variable}
              type="button"
              onClick={() => insertVariable(variable)}
              className="px-2 py-0.5 rounded text-xs font-mono transition-opacity hover:opacity-80"
              style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}
              data-testid={`${testIdPrefix}-var-${variable.replace(/[{}]/g, '')}`}
            >
              {variable}
            </button>
          ))}
        </div>
      </FormField>
    </>
  );
};

NotifyContactFields.displayName = 'NotifyContactFields';
